import { useQuery } from "convex/react";
import { CalendarCheck } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { formatAdjustmentKcal, formatShortDate, type WeeklyCheckinRow } from "./utils";

/** Minimal structural row the adjustments list renders (satisfied by CheckinView and WeeklyCheckinRow). */
export interface AdjustmentRow {
  readonly createdAt: number;
  readonly weekStartDate?: number;
  readonly adjustmentKcal: number;
  readonly paused: boolean;
  readonly explanation: string;
}

/** Presentational adjustments list — data comes from the caller so demo mode never queries the engine. */
export function AdjustmentsListView({ rows }: { readonly rows: readonly AdjustmentRow[] }) {
  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="adjustments-card">
      <div>
        <h3 className="text-base font-semibold text-text">Weekly adjustments</h3>
        <p className="mt-1 text-sm text-text-muted">How your plan adapted after each weekly check-in.</p>
      </div>
      {rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center" data-testid="adjustments-empty">
          <div className="rounded-full bg-accent-light p-3 text-accent">
            <CalendarCheck size={22} strokeWidth={2} />
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-text-muted">
            No programme adjustments yet — they appear after your first full week of data.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => {
            const weekStart = row.weekStartDate ?? row.createdAt;
            return (
              <div
                key={`${weekStart}-${row.createdAt}`}
                className="rounded-xl bg-muted/60 px-3 py-2"
                data-testid={`adjustment-${weekStart}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold text-text">Week of {formatShortDate(weekStart)}</span>
                  <span
                    className={cn(
                      "text-xs font-bold",
                      row.paused ? "text-text-muted" : row.adjustmentKcal > 0 ? "text-accent" : "text-warning",
                    )}
                  >
                    {row.paused ? "Paused" : formatAdjustmentKcal(row.adjustmentKcal)}
                  </span>
                </div>
                {row.explanation.trim().length > 0 && (
                  <p className="mt-1 text-[11px] leading-relaxed text-text-muted">{row.explanation}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/** Query-wired variant for authenticated screens (Progress). */
export function AdjustmentsList() {
  const data = useQuery(api.engine.getWeeklyCheckins, { limit: 6 });

  if (data === undefined) {
    return <Skeleton className="h-36 rounded-2xl" data-testid="adjustments-loading" />;
  }

  return <AdjustmentsListView rows={data as WeeklyCheckinRow[]} />;
}
